/**
 * Medical Navigation Context
 * Preserves existing medical workflow sequences and provides navigation state management
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { 
  MedicalNavigationState, 
  MedicalWorkflowState, 
  BreadcrumbItem,
  ClinicalBreadcrumbConfig 
} from '@/types/routing';
import { medicalRoutes, workflowSequences, deepLinkConfig } from '@/utils/routeConfig';

// Action types for navigation state management
type NavigationAction = 
  | { type: 'SET_CURRENT_ROUTE'; payload: string }
  | { type: 'SET_PATIENT_CONTEXT'; payload: { patientId: string; patientName?: string } }
  | { type: 'SET_STUDY_CONTEXT'; payload: { studyId: string; studyDate?: string; description?: string } }
  | { type: 'ADD_WORKFLOW_STEP'; payload: MedicalWorkflowState }
  | { type: 'UPDATE_RECENT_PATIENTS'; payload: string[] }
  | { type: 'UPDATE_RECENT_STUDIES'; payload: Array<{ patientId: string; studyId: string; lastAccessed: string }> }
  | { type: 'ADD_ACTIVE_REPORT'; payload: { patientId: string; reportId: string; status: 'draft' | 'in-progress' | 'review' } }
  | { type: 'REMOVE_ACTIVE_REPORT'; payload: string }
  | { type: 'CLEAR_CONTEXT' };

// Initial navigation state
const initialState: MedicalNavigationState = {
  currentRoute: '/',
  previousRoute: '/',
  workflowHistory: [],
  patientContext: {
    recentPatients: [],
    bookmarkedPatients: []
  },
  studyContext: {
    activeStudies: [],
    recentStudies: []
  },
  reportContext: {
    activeReports: [],
    recentReports: []
  }
};

// Navigation reducer
function navigationReducer(state: MedicalNavigationState, action: NavigationAction): MedicalNavigationState {
  switch (action.type) {
    case 'SET_CURRENT_ROUTE':
      return {
        ...state,
        previousRoute: state.currentRoute,
        currentRoute: action.payload
      };
      
    case 'SET_PATIENT_CONTEXT':
      return {
        ...state,
        patientContext: {
          ...state.patientContext,
          currentPatientId: action.payload.patientId,
          recentPatients: [
            action.payload.patientId,
            ...state.patientContext.recentPatients.filter(id => id !== action.payload.patientId)
          ].slice(0, 10) // Keep last 10 patients
        }
      };
      
    case 'SET_STUDY_CONTEXT':
      const newStudyEntry = {
        patientId: state.patientContext.currentPatientId || '',
        studyId: action.payload.studyId,
        lastAccessed: new Date().toISOString()
      };
      
      return {
        ...state,
        studyContext: {
          ...state.studyContext,
          currentStudyId: action.payload.studyId,
          recentStudies: [
            newStudyEntry,
            ...state.studyContext.recentStudies.filter(s => s.studyId !== action.payload.studyId)
          ].slice(0, 20) // Keep last 20 studies
        }
      };
      
    case 'ADD_WORKFLOW_STEP':
      return {
        ...state,
        workflowHistory: [
          ...state.workflowHistory,
          action.payload
        ].slice(-50) // Keep last 50 workflow steps
      };
      
    case 'ADD_ACTIVE_REPORT':
      const newReport = {
        ...action.payload,
        lastModified: new Date().toISOString()
      };
      
      return {
        ...state,
        reportContext: {
          ...state.reportContext,
          activeReports: [
            newReport,
            ...state.reportContext.activeReports.filter(r => r.reportId !== action.payload.reportId)
          ]
        }
      };
      
    case 'REMOVE_ACTIVE_REPORT':
      return {
        ...state,
        reportContext: {
          ...state.reportContext,
          activeReports: state.reportContext.activeReports.filter(r => r.reportId !== action.payload),
          recentReports: [action.payload, ...state.reportContext.recentReports].slice(0, 10)
        }
      };
      
    case 'CLEAR_CONTEXT':
      const { currentPatientId, ...restPatientContext } = state.patientContext;
      const { currentStudyId, ...restStudyContext } = state.studyContext;
      return {
        ...state,
        patientContext: restPatientContext,
        studyContext: restStudyContext
      };
      
    default:
      return state;
  }
}

// Medical Navigation Context
interface MedicalNavigationContextType {
  state: MedicalNavigationState;
  navigateToPatient: (patientId: string, patientName?: string) => void;
  navigateToStudy: (patientId: string, studyId: string, studyData?: any) => void;
  navigateToReport: (patientId: string, reportId?: string, reportType?: string) => void;
  navigateToDicom: (patientId: string, studyId: string, instanceId?: string) => void;
  generateBreadcrumbs: (config?: Partial<ClinicalBreadcrumbConfig>) => BreadcrumbItem[];
  getCurrentWorkflowStep: () => MedicalWorkflowState['currentStep'] | null;
  getNextSteps: () => string[];
  getPreviousSteps: () => string[];
  addActiveReport: (patientId: string, reportId: string, status: 'draft' | 'in-progress' | 'review') => void;
  removeActiveReport: (reportId: string) => void;
  clearContext: () => void;
}

const MedicalNavigationContext = createContext<MedicalNavigationContextType | null>(null);

// Medical Navigation Provider
export const MedicalNavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(navigationReducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Update current route when location changes
  useEffect(() => {
    dispatch({ type: 'SET_CURRENT_ROUTE', payload: location.pathname });
  }, [location.pathname]);

  // Update patient context when patientId param changes
  useEffect(() => {
    if (params.patientId && params.patientId !== state.patientContext.currentPatientId) {
      dispatch({ 
        type: 'SET_PATIENT_CONTEXT', 
        payload: { patientId: params.patientId } 
      });
    }
  }, [params.patientId, state.patientContext.currentPatientId]);

  // Update study context when studyId param changes
  useEffect(() => {
    if (params.studyId && params.studyId !== state.studyContext.currentStudyId) {
      dispatch({ 
        type: 'SET_STUDY_CONTEXT', 
        payload: { studyId: params.studyId } 
      });
    }
  }, [params.studyId, state.studyContext.currentStudyId]);

  // Navigation functions preserving existing URL structure
  const navigateToPatient = useCallback((patientId: string, patientName?: string) => {
    const path = deepLinkConfig.patientDetail(patientId);
    navigate(path);
    const payload = patientName ? { patientId, patientName } : { patientId };
    dispatch({ 
      type: 'SET_PATIENT_CONTEXT', 
      payload 
    });
  }, [navigate]);

  const navigateToStudy = useCallback((patientId: string, studyId: string, studyData?: any) => {
    const path = deepLinkConfig.studyViewer(patientId, studyId);
    navigate(path);
    dispatch({ 
      type: 'SET_PATIENT_CONTEXT', 
      payload: { patientId } 
    });
    dispatch({ 
      type: 'SET_STUDY_CONTEXT', 
      payload: { studyId, ...studyData } 
    });
  }, [navigate]);

  const navigateToReport = useCallback((patientId: string, reportId?: string, reportType?: string) => {
    let path: string;
    
    if (reportId) {
      path = deepLinkConfig.reportEditor(patientId, reportId);
    } else if (reportType) {
      path = deepLinkConfig.reportForm(patientId, reportType);
    } else {
      path = deepLinkConfig.reportBuilder(patientId);
    }
    
    navigate(path);
    dispatch({ 
      type: 'SET_PATIENT_CONTEXT', 
      payload: { patientId } 
    });
  }, [navigate]);

  const navigateToDicom = useCallback((patientId: string, studyId: string, instanceId?: string) => {
    const path = deepLinkConfig.dicomViewer(patientId, studyId, instanceId);
    navigate(path);
    dispatch({ 
      type: 'SET_PATIENT_CONTEXT', 
      payload: { patientId } 
    });
    dispatch({ 
      type: 'SET_STUDY_CONTEXT', 
      payload: { studyId } 
    });
  }, [navigate]);

  // Generate breadcrumbs based on current route and medical workflow
  const generateBreadcrumbs = useCallback((config: Partial<ClinicalBreadcrumbConfig> = {}): BreadcrumbItem[] => {
    const defaultConfig: ClinicalBreadcrumbConfig = {
      showPatientContext: true,
      showStudyContext: true,
      showWorkflowProgress: true,
      maxItems: 5,
      compactMode: false,
      ...config
    };

    const breadcrumbs: BreadcrumbItem[] = [];

    // Add dashboard link
    breadcrumbs.push({
      label: 'Dashboard',
      href: '/',
      isActive: location.pathname === '/',
      workflowStep: 'patient-selection',
      icon: 'home'
    });

    // Add patient context if available
    if (defaultConfig.showPatientContext && params.patientId) {
      breadcrumbs.push({
        label: 'Pacientes',
        href: '/patients',
        isActive: false,
        workflowStep: 'patient-selection',
        icon: 'users'
      });

      breadcrumbs.push({
        label: `Paciente ${params.patientId}`,
        href: deepLinkConfig.patientDetail(params.patientId),
        isActive: location.pathname === deepLinkConfig.patientDetail(params.patientId),
        workflowStep: 'study-review',
        patientContext: params.patientId,
        icon: 'user'
      });
    }

    // Add study context if available
    if (defaultConfig.showStudyContext && params.studyId) {
      breadcrumbs.push({
        label: `Estudio ${params.studyId}`,
        href: deepLinkConfig.studyViewer(params.patientId!, params.studyId),
        isActive: location.pathname === deepLinkConfig.studyViewer(params.patientId!, params.studyId),
        workflowStep: 'measurement',
        studyContext: params.studyId,
        icon: 'scan'
      });
    }

    // Add current page if different from contexts
    const currentRoute = medicalRoutes.find(route => {
      const routePattern = route.path.replace(/:[\w]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(location.pathname);
    });

    if (currentRoute && breadcrumbs[breadcrumbs.length - 1]?.href !== location.pathname) {
      breadcrumbs.push({
        label: currentRoute.breadcrumbLabel,
        href: location.pathname,
        isActive: true,
        workflowStep: currentRoute.workflowStep,
        icon: 'activity'
      });
    }

    // Apply compact mode if needed
    if (defaultConfig.compactMode && breadcrumbs.length > defaultConfig.maxItems) {
      const firstBreadcrumb = breadcrumbs[0];
      if (firstBreadcrumb) {
        return [
          firstBreadcrumb,
          { 
            label: '...', 
            href: '#', 
            isActive: false, 
            workflowStep: 'patient-selection',
            icon: 'more-horizontal'
          },
          ...breadcrumbs.slice(-2)
        ];
      }
    }

    return breadcrumbs.slice(0, defaultConfig.maxItems);
  }, [location.pathname, params]);

  // Get current workflow step
  const getCurrentWorkflowStep = useCallback((): MedicalWorkflowState['currentStep'] | null => {
    const currentRoute = medicalRoutes.find(route => {
      const routePattern = route.path.replace(/:[\w]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(location.pathname);
    });
    
    return currentRoute?.workflowStep || null;
  }, [location.pathname]);

  // Get next possible steps based on current workflow
  const getNextSteps = useCallback((): string[] => {
    const currentStep = getCurrentWorkflowStep();
    if (!currentStep) return [];

    // Find current step in workflow sequences
    for (const [_, sequence] of Object.entries(workflowSequences)) {
      const currentIndex = sequence.indexOf(currentStep);
      if (currentIndex !== -1 && currentIndex < sequence.length - 1) {
        return sequence.slice(currentIndex + 1);
      }
    }
    
    return [];
  }, [getCurrentWorkflowStep]);

  // Get previous steps
  const getPreviousSteps = useCallback((): string[] => {
    const currentStep = getCurrentWorkflowStep();
    if (!currentStep) return [];

    // Find current step in workflow sequences  
    for (const [_, sequence] of Object.entries(workflowSequences)) {
      const currentIndex = sequence.indexOf(currentStep);
      if (currentIndex > 0) {
        return sequence.slice(0, currentIndex);
      }
    }
    
    return [];
  }, [getCurrentWorkflowStep]);

  // Report management functions
  const addActiveReport = useCallback((patientId: string, reportId: string, status: 'draft' | 'in-progress' | 'review') => {
    dispatch({
      type: 'ADD_ACTIVE_REPORT',
      payload: { patientId, reportId, status }
    });
  }, []);

  const removeActiveReport = useCallback((reportId: string) => {
    dispatch({
      type: 'REMOVE_ACTIVE_REPORT',
      payload: reportId
    });
  }, []);

  const clearContext = useCallback(() => {
    dispatch({ type: 'CLEAR_CONTEXT' });
  }, []);

  const contextValue: MedicalNavigationContextType = {
    state,
    navigateToPatient,
    navigateToStudy,
    navigateToReport,
    navigateToDicom,
    generateBreadcrumbs,
    getCurrentWorkflowStep,
    getNextSteps,
    getPreviousSteps,
    addActiveReport,
    removeActiveReport,
    clearContext
  };

  return (
    <MedicalNavigationContext.Provider value={contextValue}>
      {children}
    </MedicalNavigationContext.Provider>
  );
};

// Hook to use medical navigation
export const useMedicalNavigation = () => {
  const context = useContext(MedicalNavigationContext);
  if (!context) {
    throw new Error('useMedicalNavigation must be used within a MedicalNavigationProvider');
  }
  return context;
};
